<template>
    <base-page>
        <CustomTable
            class=""
            :module-name="moduleName"
            :module-name-singular="moduleNameSingular"
            :response="response"
            :isLoading="isLoading"
            :tableHeaders="tableHeaders"
            :filters="contingencyPaymentFilters"
            :bulkOperations="logBulkOperations"
            :menuOptions="menuOptions"
            default-filter-display="all"
            :defaultOrdering="defaultOrdering"
            :shouldReload="shouldReload"
            :shouldClearSelected="shouldClearSelected"
            :bulkOperationResponse="bulkOperationResponse"
            :reorder-bulk-operations="reorderBulkOperations"
            :reorder-filters="reorderFilters"
            createModalSize="w-3/4 h-5/6"
            @refetch-results="loadContingencyPayments"
            @clear-filter="clearFilter"
            @change-filters="setFilters"
            @set-filter-options="setFilterOptions"
            @toggle-pinned-filter="togglePinnedFilter"
        >
            <!-- <template #create-form="{ events: { closeCreateForm, refetchResults }, fetchFilter }">
                <CreateCompany
                    :client-options="clientOptions"
                    @refetch-results="refetchResults(fetchFilter)"
                    @close="closeCreateForm"
                />
            </template> -->
            <template #table-row="{ events: { addSelectedResult, removeSelectedResult, refetchResults, openRowDetails }, selectAll, expandAll, fetchFilter, userPermissions, results, clearedSelectedResults }">
                <ContingencyPaymentRow
                    v-for="contingencyPayment in results"
                    :key="contingencyPayment.id"
                    :selectAll="selectAll"
                    :expandAll="expandAll"
                    :userPermissions="userPermissions"
                    :clearedSelectedResults="clearedSelectedResults"
                    :payment="contingencyPayment"
                    @refetch-results="refetchResults(fetchFilter)"
                    @select-row="addSelectedResult"
                    @deselect-row="removeSelectedResult"
                    @open-row-details="openRowDetails"
                    @perform-row-action="performRowAction"
                />
                <tr class="bg-gray-200 h-60">
                    <td :colspan="columnSpan" :class="{ 'border-t border-gray-300' : results && results.length > 0}"></td>
                </tr>
            </template>
            <!-- <template #details-page="{ events: { closeDetails, refetchResults }, resultRow, userPermissions, fetchFilter }">
                <CompanyDetails
                    :company="resultRow"
                    :user-permissions="userPermissions"
                    :client-options="clientOptions"
                    @close-details="closeDetails"
                    @refetch-results="refetchResults(fetchFilter)"
                />
            </template> -->
            <template #table-footer="{ events: { getNext, goToFirst, getPrev, goToLast, jumpToPage, setPageSizeFilter }, viewingFromNbr, viewingToNbr, count, selectedResults, totalPages, page }">
                <CompanyFooter
                    :is-loading="isLoading"
                    :viewingFromNbr="viewingFromNbr"
                    :viewingToNbr="viewingToNbr"
                    :count="count"
                    :selectedResults="selectedResults"
                    :numPages="totalPages"
                    :page="page"
                    :column-span="columnSpan"
                    @get-next="getNext"
                    @go-to-first="goToFirst"
                    @get-prev="getPrev"
                    @go-to-last="goToLast"
                    @jump-to-page="jumpToPage"
                    @set-page-size-filter="setPageSizeFilter"
                />
            </template>
        </CustomTable>
        <base-modal :is-form-visible="visibleEditForm" @close="closeEditForm">
          <template #header>
            <h2 class="text-lg">Payment on {{ formatDateTwoDigitYear(displayedContingencyPayment.payment_date) }} ID: {{ displayedContingencyPayment.id }}</h2>
          </template>
          <template #body>
            <EditContingencyPayment
              :payment="displayedContingencyPayment"
              @close="closeEditForm"
              @refetch-payments="reloadContingencyPayments"
            />
          </template>
        </base-modal>
    </base-page>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import CustomTable from '../base-components/CustomTable.vue';
import ContingencyPaymentRow from './ContingencyPaymentRow.vue';
import CompanyFooter from '../company/CompanyFooter.vue';
import { getContingencyPayments } from '@/services/ContingencyPayment';
import { getClients } from '@/services/Client';
import { formatDateTwoDigitYear } from '@/composables/formatDate';
import EditContingencyPayment from './EditContingencyPayment.vue';

const store = useStore()
const $toast = useToast()

const isLoading = ref(false)
const moduleName = ref('Assimilation Logs')
const moduleNameSingular = ref('Assimilation Log');
const response = ref(null)
const defaultOrdering = ref([])
const shouldReload = ref(false)
const shouldClearSelected = ref(false)
const bulkOperationResponse = ref()
const reorderBulkOperations = ref(false)
const reorderFilters = ref(false)
const displayedLog = ref(null)
const visibleEditForm = ref(false)
const actionButtonId = ref(null)
const tableHeaders = ref([
    {
        value: '',
        label: '',
        class: 'p-0 w-[5%] bg-gray-150 border-b-2'
    },
    {
        value: 'contingency_payment',
        label: 'Contingency Payment',
        class: 'p-0 w-[40%] bg-gray-150 border-b-2'
    },
    {
        value: 'transaction',
        label: 'Transaction',
        class: 'p-0 w-[40%] bg-gray-150 border-b-2'
    },
    {
        value: 'tombstone',
        label: 'Tombstone',
        class: 'p-0 w-[15%] bg-gray-150 border-b-2',
    }
])

const menuOptions = reactive({
    controlPanel: {
        width: 'w-[330px]',
        panes: ['filters']
    },
    quickSearch: false,
    selectAll: false,
    selectAllHeader: false,
    expandAllHeader: false,
    paginationArrows: false,
    totalResults: false,
    pageSize: false,
    refreshButton: false,
    createForm: false,
    addFormRows: false,
})

const contingencyPaymentFilters = reactive({
    client: {
        id: 22,
        name: "client",
        label: "Client",
        value: [],
        defaultValue: [],
        placeholder: '',
        options: [
            {value: "clear", label: "------------"}
        ],
        widget: 'list-box',
        multiSelect: true,
        setOptionsMethod: loadClientOptions.bind(null),
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
})

const clientOptions = ref([])

async function loadClientOptions() {
    // console.log('loadClientOptions')
    const token = store.state.auth.token
    const response = await getClients({token: token})
    clientOptions.value = response.data.results.map((client) => {
        return {value: client.id, label: client.client_name}
    })
    contingencyPaymentFilters.client.options = [{value: "clear", label: "------------"}, ...response.clients.map((client) => {
        return {
            label: client.client_name, 
            value: client.id,
        }
    })];
}

const logBulkOperations = reactive({})

const displayedContingencyPayment = ref(null)

const contingencyPaymentRowActions = reactive({
    openEditForm: {
        performAction(actionData) {
            displayedContingencyPayment.value = actionData.payment
            visibleEditForm.value = true
        }
    },
})

const logSummaryActions = reactive({})

const columnSpan = computed(() => {
    let numHeaders = tableHeaders.value.length
    return menuOptions.selectAllHeader ? numHeaders + 1 : numHeaders
})

function handleActionResponse(response, buttonId, errorMessage) {
    console.log(response)
    if(response.status == 200) {
        $toast.open({
            type: 'success',
            duration: 5000,
            message: response.data.status ? response.data.status : 'Successfully completed action.',
        })
        reloadContingencyPayments()
    } else {
        $toast.open({
            type: 'error',
            duration: 5000,
            message: errorMessage
        })
    }
    actionButtonId.value = buttonId
}

function reloadContingencyPayments() {
    shouldReload.value = !shouldReload.value
}

function setFilters(updatedFilters) {
    contingencyPaymentFilters.client = updatedFilters.client;
}

function clearFilter(filterKey) {
    if(filterKey.endsWith('.min')) {
        contingencyPaymentFilters[filterKey.replace('.min', '')].min.value = null
    } else if(filterKey.endsWith('.max')) {
            contingencyPaymentFilters[filterKey.replace('.max', '')].max.value = null
    } else if(Array.isArray(contingencyPaymentFilters[filterKey].value)){
        contingencyPaymentFilters[filterKey].value = []
    } else {
        contingencyPaymentFilters[filterKey].value = null
    }
}


function closeEditForm() {
    visibleEditForm.value = false
    displayedLog.value = null
}

function performRowAction(actionData) {
    actionButtonId.value = null
    // console.log('CompanyPage: performRowAction', actionData)
    contingencyPaymentRowActions[actionData.actionKey].performAction(actionData)
}

async function setFilterOptions(filterName, queryParams='') {
    // console.log('CompanyPage: setFilterOptions', filterName)
    if(!contingencyPaymentFilters[filterName].awaitingOptions){
        if(contingencyPaymentFilters[filterName].setOptionsMethod){
            contingencyPaymentFilters[filterName].awaitingOptions = true
            contingencyPaymentFilters[filterName].setOptionsMethod(queryParams)
            setTimeout(() => {
                contingencyPaymentFilters[filterName].awaitingOptions = false;
            }, 30000)
        }
    }
}


async function loadContingencyPayments(fetchFilter='') {
    isLoading.value = true;
    const token = store.state.auth.token
    response.value = await getContingencyPayments(token, fetchFilter);
    isLoading.value = false;
}

loadContingencyPayments()

// async function loadPinnedFilters() {
//     // console.log('loadPinnedFilters')
//     const token = store.state.auth.token
//     const userId = store.state.auth.user.id
//     const response = await getPinnedUserCompanyFilterFields(token, userId)
//     // console.log(response)
//     setPinnedFilters(response.data)
// }

async function togglePinnedFilter(filterKey) {
    // console.log('togglePinnedFilter', filterKey)
    let pinnedValue = contingencyPaymentFilters[filterKey].pinned
    contingencyPaymentFilters[filterKey].pinned = !contingencyPaymentFilters[filterKey].pinned
    reorderFilters.value = !reorderFilters.value
    // if(pinnedValue) {
    //     await persistUnpinnedFilter(contingencyPaymentFilters[filterKey])
    // } else {
    //     await persistPinnedFilter(contingencyPaymentFilters[filterKey])
    // }
    // loadPinnedFilters()
}
function fetchAllOptions() {
    loadClientOptions()
}
  
fetchAllOptions()

</script>